import React, { useState, useEffect, useCallback } from 'react';
import { collectionsAPI } from '../../lib/api';
import { useCurrency } from '../../lib/CurrencyContext';
import { formatCurrencyCompact, formatCurrency } from '../../lib/currency';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ScrollArea } from '../ui/scroll-area';
import { Skeleton } from '../ui/skeleton';
import {
  AlertTriangle, Search, ChevronRight, ArrowUpDown, Filter, Download
} from 'lucide-react';
import { toast } from 'sonner';

import { AgingBucketCards, AgingDistributionBar } from './collections/AgingBuckets';
import { CollectionDetailSheet } from './collections/CollectionDetailSheet';

const STATUS_CONFIG = {
  new: { label: 'New', color: 'bg-slate-100 text-slate-700' },
  contacted: { label: 'Contacted', color: 'bg-blue-100 text-blue-700' },
  promised: { label: 'Promised', color: 'bg-emerald-100 text-emerald-700' },
  disputed: { label: 'Disputed', color: 'bg-amber-100 text-amber-700' },
  partial_payment: { label: 'Partial', color: 'bg-teal-100 text-teal-700' },
  escalated: { label: 'Escalated', color: 'bg-red-100 text-red-700' },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-700' },
  written_off: { label: 'Written Off', color: 'bg-gray-100 text-gray-500' },
};

export function CollectionsTab() {
  const { currency: selectedCurrency } = useCurrency();
  const [agingSummary, setAgingSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeBucket, setActiveBucket] = useState(null);
  const [activeStatus, setActiveStatus] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('aging');
  const [detailSheet, setDetailSheet] = useState(null);
  const [actions, setActions] = useState([]);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [collectors, setCollectors] = useState([]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (activeBucket) params.bucket = activeBucket;
      if (activeStatus) params.status = activeStatus;
      if (sortBy) params.sort_by = sortBy;

      const [summaryRes, invoicesRes, collectorsRes] = await Promise.all([
        collectionsAPI.getAgingSummary(),
        collectionsAPI.getOverdueInvoices(params),
        collectionsAPI.getCollectors(),
      ]);

      setAgingSummary(summaryRes.data);
      setInvoices(invoicesRes.data?.items || []);
      setCollectors(collectorsRes.data?.collectors || []);
    } catch (err) {
      toast.error('Failed to load collections data');
    } finally {
      setLoading(false);
    }
  }, [activeBucket, activeStatus, sortBy]);

  useEffect(() => { loadData(); }, [loadData]);

  const openDetail = async (inv) => {
    setDetailSheet(inv);
    setActionsLoading(true);
    try {
      const res = await collectionsAPI.getCollectionActions(inv.invoice_number);
      setActions(res.data || []);
    } catch { setActions([]); }
    setActionsLoading(false);
  };

  const filteredInvoices = invoices.filter(inv => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      inv.invoice_number?.toLowerCase().includes(q) ||
      inv.account_name?.toLowerCase().includes(q) ||
      inv.salesperson?.toLowerCase().includes(q)
    );
  });

  const exportCollections = () => {
    if (filteredInvoices.length === 0) return;
    const rows = filteredInvoices.map(inv => ({
      Invoice: inv.invoice_number, Account: inv.account_name, Salesperson: inv.salesperson,
      Currency: inv.currency || '', 'Amount Total': inv.amount_total, 'Amount Due': inv.amount_residual,
      'Due Date': inv.due_date,
      'Aging Days': inv.aging_days, 'Aging Bucket': inv.aging_label, Status: inv.collection_status,
      'Assigned To': inv.assigned_to, 'Promised Date': inv.promised_date,
      'Actions Count': inv.action_count, 'Last Action': inv.last_action_date,
    }));
    const headers = Object.keys(rows[0]);
    const csv = [headers.join(','), ...rows.map(r => headers.map(h => `"${r[h] || ''}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'collections_export.csv'; a.click();
    URL.revokeObjectURL(url);
    toast.success('Export complete');
  };

  if (loading && !agingSummary) {
    return (
      <div className="space-y-4" data-testid="collections-loading">
        <div className="grid grid-cols-5 gap-3">
          {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="collections-tab">
      {/* Summary Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Pending Collections
          </h3>
          <p className="text-sm text-muted-foreground">
            {agingSummary?.total_count || 0} outstanding invoices totaling{' '}
            <span className="font-semibold text-foreground">{formatCurrencyCompact(agingSummary?.total_outstanding, agingSummary?.base_currency || selectedCurrency)}</span>
            {agingSummary?.currency_breakdown && Object.keys(agingSummary.currency_breakdown).length > 1 && (
              <span className="ml-1 text-xs">
                ({Object.entries(agingSummary.currency_breakdown).map(([cur, data], i) => (
                  <span key={cur}>{i > 0 ? ' + ' : ''}{formatCurrencyCompact(data.amount, cur)}</span>
                ))})
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportCollections} data-testid="export-collections-btn">
            <Download className="h-4 w-4 mr-1" /> Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setActiveBucket(null); setActiveStatus(null); }} data-testid="clear-collections-filters">
            Clear Filters
          </Button>
        </div>
      </div>

      {/* Aging Buckets & Distribution */}
      <AgingBucketCards agingSummary={agingSummary} activeBucket={activeBucket} setActiveBucket={setActiveBucket} currency={agingSummary?.base_currency || selectedCurrency} />
      <AgingDistributionBar agingSummary={agingSummary} activeBucket={activeBucket} setActiveBucket={setActiveBucket} currency={agingSummary?.base_currency || selectedCurrency} />

      {/* Collection Status Summary */}
      {agingSummary && Object.keys(agingSummary.status_summary || {}).length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="status-summary">
          {Object.entries(agingSummary.status_summary).map(([status, count]) => {
            const cfg = STATUS_CONFIG[status] || {};
            return (
              <Badge
                key={status}
                className={`cursor-pointer ${activeStatus === status ? 'ring-2 ring-offset-1 ring-primary' : ''} ${cfg.color || 'bg-gray-100 text-gray-700'}`}
                onClick={() => setActiveStatus(activeStatus === status ? null : status)}
              >
                {cfg.label || status}: {count}
              </Badge>
            );
          })}
        </div>
      )}

      {/* Filters + Search */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search by invoice, account, or salesperson..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 h-9" data-testid="collections-search" />
        </div>
        <Select value={activeStatus || 'all'} onValueChange={v => setActiveStatus(v === 'all' ? null : v)}>
          <SelectTrigger className="w-[150px] h-9" data-testid="collections-status-filter">
            <Filter className="h-3 w-3 mr-1" /><SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => <SelectItem key={key} value={key}>{cfg.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[140px] h-9" data-testid="collections-sort">
            <ArrowUpDown className="h-3 w-3 mr-1" /><SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="aging">By Aging</SelectItem>
            <SelectItem value="amount">By Amount</SelectItem>
            <SelectItem value="account">By Account</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground ml-auto">{filteredInvoices.length} of {invoices.length} shown</span>
      </div>

      {/* Invoice Table */}
      <Card>
        <CardContent className="p-0">
          <ScrollArea className="h-[500px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[130px]">Invoice</TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead>Salesperson</TableHead>
                  <TableHead className="text-right">Amount Due</TableHead>
                  <TableHead className="w-[50px]">Cur</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead className="text-center">Aging</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Assigned To</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-10 text-muted-foreground">No outstanding invoices in this scope</TableCell>
                  </TableRow>
                ) : (
                  filteredInvoices.map((inv, idx) => {
                    const stCfg = STATUS_CONFIG[inv.collection_status] || STATUS_CONFIG.new;
                    return (
                      <TableRow key={`${idx}-${inv.invoice_number}`} className="cursor-pointer hover:bg-muted/50" onClick={() => openDetail(inv)} data-testid={`collection-row-${inv.invoice_number}`}>
                        <TableCell className="font-medium text-sm">{inv.invoice_number}</TableCell>
                        <TableCell className="text-sm max-w-[160px] truncate">{inv.account_name}</TableCell>
                        <TableCell className="text-sm max-w-[130px] truncate">{inv.salesperson || '-'}</TableCell>
                        <TableCell className="text-right font-mono font-semibold text-sm">{formatCurrencyCompact(inv.amount_residual, inv.currency || selectedCurrency)}</TableCell>
                        <TableCell>
                          {inv.currency && inv.currency !== 'OMR' ? (
                            <Badge variant="outline" className="text-[9px] font-bold">{inv.currency}</Badge>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-sm">{inv.due_date || '-'}</TableCell>
                        <TableCell className="text-center">
                          <Badge className="text-[10px] font-bold" style={{ backgroundColor: inv.aging_color, color: '#fff' }}>{inv.aging_days}d</Badge>
                        </TableCell>
                        <TableCell><Badge className={`text-[10px] ${stCfg.color}`}>{stCfg.label}</Badge></TableCell>
                        <TableCell className="text-sm">{inv.assigned_to || '-'}</TableCell>
                        <TableCell className="text-center">
                          {inv.action_count > 0 ? <Badge variant="secondary" className="text-[10px]">{inv.action_count}</Badge> : <span className="text-xs text-muted-foreground">-</span>}
                        </TableCell>
                        <TableCell><ChevronRight className="h-4 w-4 text-muted-foreground" /></TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Detail Sheet */}
      <CollectionDetailSheet
        detailSheet={detailSheet}
        setDetailSheet={setDetailSheet}
        collectors={collectors}
        actions={actions}
        actionsLoading={actionsLoading}
        onRefresh={loadData}
        setActions={setActions}
        currency={selectedCurrency}
      />
    </div>
  );
}
